import React from 'react';
import { EMPTY_ICON_BOX, EMPTY_ICON_BLUR } from './utils/uiTokens';

/**
 * Empty state canonical del modulo de Proyectos.
 *
 * Patron visual (Linear/Notion minimal):
 *   - Cuadro redondeado de 56x56 con icono al centro
 *   - Halo difuso cyan detras del cuadro (decora sin gritar)
 *   - Titulo en slate-900 (1 linea, ~5 palabras)
 *   - Descripcion en slate-500 (2 lineas max, ~80-120 chars)
 *   - CTA opcional debajo, alineado al centro
 *
 * Modos:
 *   variant="full"     (default) -> overlay absoluto centrado en el viewport
 *   variant="centered" -> solo el contenido centrado, sin padding ni overlay
 *                         (para meter dentro de wrappers que ya tienen su layout)
 *   variant="card"     -> compacto sin halo (sidebars, dropdowns, secciones)
 *
 * Como usarlo:
 *   <EmptyState
 *     icon={Inbox}
 *     title="Sin tareas asignadas"
 *     description="Cuando alguien te asigne una tarea va a aparecer acá."
 *     action={<button className={BUTTON.primary + ' ' + BUTTON.size.md}>Nueva tarea</button>}
 *   />
 */
const EmptyState = ({
  icon: Icon,
  title,
  description,
  action = null,
  variant = 'full',
  className = '',
}) => {
  // Modo "full" usa positioning absoluto para centrar en el viewport del padre.
  // El padre debe tener `relative` y un alto computable.
  if (variant === 'full') {
    return (
      <div className={`absolute inset-0 flex items-center justify-center pointer-events-none ${className}`}>
        <div className="text-center px-6 pointer-events-auto">
          <IconBlock Icon={Icon} withHalo />
          <Heading title={title} description={description} />
          {action && <div className="mt-4 inline-flex">{action}</div>}
        </div>
      </div>
    );
  }

  if (variant === 'centered') {
    return (
      <div className={`text-center ${className}`}>
        <IconBlock Icon={Icon} withHalo />
        <Heading title={title} description={description} />
        {action && <div className="mt-4 inline-flex">{action}</div>}
      </div>
    );
  }

  // variant="card" — compacto, sin halo, icono mas chico, copy minimal.
  return (
    <div className={`py-8 px-4 text-center ${className}`}>
      {Icon && (
        <Icon size={20} className="text-slate-300 mx-auto mb-2" strokeWidth={1.5} />
      )}
      {title && (
        <p className="text-sm text-slate-500">{title}</p>
      )}
      {description && (
        <p className="text-xs text-slate-400 mt-1">{description}</p>
      )}
      {action && <div className="mt-3 inline-flex">{action}</div>}
    </div>
  );
};

const IconBlock = ({ Icon, withHalo }) => {
  if (!Icon) return null;
  return (
    <div className="relative mb-5 inline-block">
      {withHalo && <div className={EMPTY_ICON_BLUR} />}
      <div className={`relative ${EMPTY_ICON_BOX}`}>
        <Icon size={24} className="text-slate-400" strokeWidth={1.5} />
      </div>
    </div>
  );
};

const Heading = ({ title, description }) => (
  <>
    {title && (
      <h3 className="text-base font-semibold text-slate-900 tracking-tight mb-1">
        {title}
      </h3>
    )}
    {description && (
      <p className="text-sm text-slate-500 max-w-sm mx-auto">
        {description}
      </p>
    )}
  </>
);

export default EmptyState;
